"use client";

import { X } from "lucide-react";
import { useState } from "react";
import { FaDiscord } from "react-icons/fa6";

import { SectionHeader } from "#/components/SectionHeader";
import { Badge } from "#/components/ui/badge";
import { Button } from "#/components/ui/button";
import { CardDescription, CardFooter, CardHeader, CardTitle } from "#/components/ui/card";
import { DISCORD_INVITE_URL } from "#/lib/discordInvite";
import { dismissDiscordPromo } from "#/server/dismissDiscordPromo";

import { PromoCardShell } from "./-premium.promoCardShell";

const discordCtaClassName =
  "mx-auto h-11 w-full max-w-sm text-base font-semibold shadow-md shadow-discord/25";

function DiscordPromoHeader() {
  return (
    <CardHeader className="relative gap-3 text-center">
      <Badge className="mx-auto bg-discord px-3 py-1 text-sm font-semibold text-discord-foreground">
        <FaDiscord className="size-3.5" aria-hidden />
        Community
      </Badge>
      <CardTitle>
        <SectionHeader className="text-3xl text-foreground sm:text-4xl">
          Daily Wars Discord
        </SectionHeader>
      </CardTitle>
      <CardDescription className="text-base text-foreground">
        Join the Daily Wars Discord to meet other commanders, discuss strategies, and have a say in
        the development of upcoming updates.
      </CardDescription>
    </CardHeader>
  );
}

function DiscordPromoActions() {
  return (
    <CardFooter className="relative flex flex-col gap-3 border-t border-discord/25 bg-discord/8 pt-6">
      <Button
        variant="discord"
        size="lg"
        className={discordCtaClassName}
        nativeButton={false}
        render={<a href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer" />}
      >
        <FaDiscord data-icon="inline-start" aria-hidden="true" />
        Join Server
      </Button>
    </CardFooter>
  );
}

function DiscordPromoDismissButton({ onDismiss }: { onDismiss: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="absolute top-2 right-2 z-10 text-white hover:bg-white/10 hover:text-white"
      aria-label="Dismiss Discord banner"
      onClick={onDismiss}
    >
      <X className="size-5" />
    </Button>
  );
}

export function DiscordPromoCard() {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) {
    return null;
  }

  const hide = () => {
    setDismissed(true);
    void dismissDiscordPromo({ data: {} }).catch(() => {});
  };

  return (
    <PromoCardShell tone="discord">
      <DiscordPromoHeader />
      <DiscordPromoActions />
      <DiscordPromoDismissButton onDismiss={hide} />
    </PromoCardShell>
  );
}
